// perlu.build — daftar tugas harian per-peran (prioritas & umur). Port perluBuild v0.18
// (baris 503–525) MURNI: terima tiket + peran + config SLA, kembalikan kategori.
import { rp, ageDays, todayISO } from '@domain/format';
import { piutangTiket } from '@domain/money';
import { slaInfo, slaBreached, fmtDurMin, financeSlaInfo, overdueDays } from '@domain/sla';
import type { Role } from '@core/types';
import type { PerluTicket } from './perlu.repo';

export interface PerluItem { t: PerluTicket; urgent: boolean; reason: string }
export interface PerluCat { key: string; label: string; color: string; items: PerluItem[] }
export interface PerluCfg {
  slaOn: boolean; slaCfg: Record<string, number>; ambangBelum: number;
  verifikasiBayarMenit: number; verifikasiRefundMenit: number; penagihanEskalasiHari: number;
  /** Kelonggaran setelah tanggal estimasi selesai sebelum disebut terlambat (hari). */
  graceEstimasiHari: number;
  /** Umur minimal "Menunggu Persetujuan" sebelum dianggap perlu dikejar (hari). */
  staleKonfirmasiHari: number;
}

/** Info SLA fase menunggu finance sebuah tiket verif (refund atau markLunas). */
function verifSla(t: PerluTicket, cfg: PerluCfg): { isRefund: boolean; breached: boolean; overMin: number } {
  const isRefund = !!(t.ticket.refund && t.ticket.refund.status === 'pending');
  const s = isRefund
    ? financeSlaInfo(t.refundStartMs, null, cfg.slaOn ? cfg.verifikasiRefundMenit : 0)
    : financeSlaInfo(t.verifyStartMs, null, cfg.slaOn ? cfg.verifikasiBayarMenit : 0);
  return { isRefund, breached: !!(s.applies && s.breached), overMin: s.overMin ?? 0 };
}

const PRIO: Record<string, number> = { P0: 0, P1: 1, P2: 2, P3: 3 };
const getPriorityNum = (p: string): number => (PRIO[p] ?? 2);
const prioLabel = (p: string): string => p || 'P2';

function overdueWork(t: PerluTicket, graceHari = 1): boolean {
  if (!['Diagnosa', 'Dikerjakan'].includes(t.status)) return false;
  if (!t.estSelesai) return false;
  const d = new Date(t.estSelesai);
  if (isNaN(d.getTime())) return false;
  return Date.now() > d.getTime() + Math.max(0, graceHari) * 86400000;
}

function sortWork(a: PerluTicket, b: PerluTicket, grace = 1): number {
  const oa = overdueWork(a, grace) ? 0 : 1, ob = overdueWork(b, grace) ? 0 : 1;
  if (oa !== ob) return oa - ob;
  const pa = getPriorityNum(a.prioritas), pb = getPriorityNum(b.prioritas);
  if (pa !== pb) return pa - pb;
  return ageDays(b.tglISO) - ageDays(a.tglISO);
}

/** Susun kategori Perlu Diperhatikan untuk `role`. Port perluBuild v0.18. */
export function perluBuild(tickets: PerluTicket[], role: Role, cfg: PerluCfg): PerluCat[] {
  const cats: PerluCat[] = [];
  const today = todayISO();

  if (['Teknisi', 'SPV', 'Manager', 'Owner'].includes(role)) {
    const work = tickets.filter((t) => ['Diagnosa', 'Dikerjakan'].includes(t.status)).sort((a, b) => sortWork(a, b, cfg.graceEstimasiHari));
    if (work.length) cats.push({
      key: 'kerja', label: '🔧 Dikerjakan / Diagnosa — dahulukan prioritas & yang paling lama', color: 'var(--blue)',
      items: work.map((t) => ({ t, urgent: overdueWork(t, cfg.graceEstimasiHari) || getPriorityNum(t.prioritas) === 0, reason: (overdueWork(t, cfg.graceEstimasiHari) ? '⏰ lewat estimasi · ' : '') + prioLabel(t.prioritas) + ' · ' + t.status + ' · ' + ageDays(t.tglISO) + ' hr' + (t.teknisi ? ' · ' + t.teknisi : '') })),
    });
  }
  if (['CS', 'SPV', 'Manager', 'Owner'].includes(role)) {
    const appr = tickets.filter((t) => t.status === 'Menunggu Persetujuan').sort((a, b) => ageDays(b.tglISO) - ageDays(a.tglISO));
    if (appr.length) cats.push({
      key: 'konfirmasi', label: '📞 Perlu dikonfirmasi ke pelanggan (persetujuan)', color: 'var(--warn)',
      items: appr.map((t) => ({ t, urgent: ageDays(t.tglISO) >= cfg.staleKonfirmasiHari, reason: 'est ' + rp(t.estBiaya) + ' · umur ' + ageDays(t.tglISO) + ' hr' })),
    });
    const siap = tickets.filter((t) => t.status === 'Siap Diambil').sort((a, b) => ageDays(b.tglISO) - ageDays(a.tglISO));
    if (siap.length) cats.push({
      key: 'siap', label: '📦 Siap diambil — hubungi pelanggan / survei', color: 'var(--ok)',
      items: siap.map((t) => ({ t, urgent: ageDays(t.tglISO) > cfg.ambangBelum, reason: (t.hasil === 'jadi' ? 'selesai' : (t.hasil === 'tidakbisa' ? 'tdk bisa' : 'batal')) + ' · umur ' + ageDays(t.tglISO) + ' hr' + ((t.hasil === 'jadi' && !t.surveyDone) ? ' · survei belum' : '') })),
    });
  }
  if (['Admin Finance', 'Manager', 'Owner'].includes(role)) {
    const verif = tickets.filter((t) => t.status === 'Sudah Diambil' && ((!!t.ticket.lunas && !t.ticket.verified) || (!!t.ticket.refund && t.ticket.refund.status === 'pending')))
      .sort((a, b) => { const ba = verifSla(a, cfg).breached ? 0 : 1, bb = verifSla(b, cfg).breached ? 0 : 1; return ba !== bb ? ba - bb : ageDays(b.tglISO) - ageDays(a.tglISO); }); // breached dulu
    if (verif.length) cats.push({
      key: 'verif', label: '✅ Perlu dicek/diverifikasi Finance (pembayaran / refund)', color: '#1FA9E0',
      items: verif.map((t) => {
        const v = verifSla(t, cfg);
        const base = v.isRefund ? ('refund ' + rp(t.ticket.refund?.amount ?? 0) + ' menunggu verifikasi') : 'ditandai LUNAS · perlu diverifikasi';
        return { t, urgent: v.breached || v.isRefund, reason: base + (v.breached ? ' · ⏰ LEWAT SLA (' + fmtDurMin(v.overMin) + ')' : '') };
      }),
    });
    const grace = cfg.slaOn ? cfg.penagihanEskalasiHari : 0;
    const tagih = tickets.filter((t) => piutangTiket(t.ticket) > 0 && t.status === 'Sudah Diambil').sort((a, b) => String(a.jatuhTempo || '9999-99-99').localeCompare(String(b.jatuhTempo || '9999-99-99')));
    if (tagih.length) cats.push({
      key: 'tagih', label: '💰 Piutang perlu ditagih (utamakan jatuh tempo lewat)', color: 'var(--danger)',
      items: tagih.map((t) => {
        const od = t.jatuhTempo ? overdueDays(t.jatuhTempo, today) : 0;
        const overGrace = od > grace, over = od > 0;
        const tag = t.jatuhTempo ? (' · jatuh tempo ' + t.jatuhTempo + (overGrace ? ' ⏰ ESKALASI (lewat ' + od + ' hr)' : (over ? ' ⏳ tempo ' + od + ' hr' : ''))) : ' · tanpa tempo';
        return { t, urgent: overGrace, reason: rp(piutangTiket(t.ticket)) + tag };
      }),
    });
  }
  // DP MENGENDAP — dulu hidup sebagai alert di Dashboard; panel itu dihapus (Apo 21 Jul),
  // jadi sinyalnya dipindah ke sini supaya tetap terdorong ke orangnya (dan ikut badge),
  // bukan menunggu ada yang iseng membuka laporan. Uang pelanggan tertahan di unit yang
  // tak kunjung diambil = kewajiban toko, bukan sekadar catatan.
  if (['Admin Finance', 'CS', 'SPV', 'Manager', 'Owner'].includes(role)) {
    const dpEndap = tickets
      .filter((t) => t.status === 'Siap Diambil' && (t.ticket.dp ?? 0) > 0 && ageDays(t.tglISO) > cfg.ambangBelum)
      .sort((a, b) => ageDays(b.tglISO) - ageDays(a.tglISO));
    if (dpEndap.length) cats.push({
      key: 'dp', label: '💵 DP mengendap — unit ber-uang-muka lama tak diambil', color: 'var(--warn)',
      items: dpEndap.map((t) => ({ t, urgent: true, reason: 'DP ' + rp(t.ticket.dp ?? 0) + ' tertahan · menunggu ' + ageDays(t.tglISO) + ' hr' })),
    });
  }
  if (['SPV', 'Manager', 'Owner'].includes(role)) {
    const slaOver = tickets.filter((t) => slaBreached(t.status, t.enterMs, t.estSelesai, cfg.slaOn, cfg.slaCfg)).sort((a, b) => sortWork(a, b, cfg.graceEstimasiHari));
    if (slaOver.length) cats.push({
      key: 'sla', label: '⏰ SLA terlewati — perlu tindak lanjut / eskalasi', color: 'var(--danger)',
      items: slaOver.map((t) => { const s = slaInfo(t.status, t.enterMs, t.estSelesai, cfg.slaOn, cfg.slaCfg); return { t, urgent: true, reason: '⏰ lewat ' + fmtDurMin(s.overMin ?? 0) + ' · status ' + t.status + (t.teknisi ? ' · ' + t.teknisi : '') }; }),
    });
  }
  return cats;
}

export const perluCount = (cats: PerluCat[]): number => cats.reduce((a, c) => a + c.items.length, 0);
